import { isEmpty } from 'lodash';

import { CoinsSwap02Icon } from '@/components/icons/CoinsSwap02Icon';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { Grid01Icon } from '@/components/icons/Grid01Icon';
import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { UserEditIcon } from '@/components/icons/UserEditIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { getEntityValuationActionsBase } from '@/modules/entities/details/EntityValuation/EntityValuation.utils';
import { renderEntityValuationActionMenuItems } from '@/modules/entities/details/EntityValuation/EntityValuationActions';
import { EntityValuationActions_EntityFragment } from '@/modules/entities/details/EntityValuation/graphql/EntityValuation.generated';
import { getEntityIntegrationActions } from '@/modules/entities/details/EntityValuation/utils/getEntityIntegrationActions';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { GroupNodeMember } from '@/modules/estateWaterfall/types';
import { usePresentationHoverActionsContext } from '@/modules/presentation/components/PresentationHoverActions/PresentationHoverActions.context';
import {
  SlideToolbarButton,
  SlideToolbarButtonWithPopover,
  SlideToolbarGroupProps,
} from '@/modules/slideEditor/SlideToolbar';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import { useViewSourceAction } from './hooks/useViewSourceAction';
import {
  PresentationEntityActionTarget,
  usePresentationEntityActionsContext,
} from './presentationEntityActions.context';
import { PresentationViewSourceCitation } from './presentationViewSource.utils';

export interface PresentationEntityActionButtonProps
  extends SlideToolbarGroupProps {
  target: PresentationEntityActionTarget;
  /**
   * Hide the toolbar. Called once an action runs (a menu item is selected or a
   * button is clicked) so the toolbar doesn't linger over the row/node.
   */
  onDismiss: () => void;
}

/**
 * "Edit entity" toolbar button. Opens a dropdown of the entity type's edit
 * sections, each deep-linking into that section of the edit-entity modal. Reads
 * its sections + handler from {@link usePresentationEntityActionsContext}; renders
 * disabled when the edit flow isn't wired up (e.g. the viewer can't edit).
 *
 * Shared by the entities-list rows and the estate-waterfall diagram nodes so
 * the edit-entity entry point lives in one place.
 */
export function EditEntityButton({
  target,
  onDismiss,
}: PresentationEntityActionButtonProps) {
  const actions = usePresentationEntityActionsContext();
  const sections = actions?.getEditEntitySections(target.entityType) ?? [];
  const onEditEntitySection = actions?.onEditEntitySection;

  if (!onEditEntitySection || sections.length === 0) {
    return (
      <SlideToolbarButton variant="transparent" startIcon={Edit02Icon} disabled>
        Edit entity
      </SlideToolbarButton>
    );
  }

  return (
    <SlideToolbarButtonWithPopover
      variant="transparent"
      startIcon={Edit02Icon}
      label="Edit entity"
      popperVariant="menuBelow"
    >
      {sections.map(({ label, section }) => (
        <MenuItem
          key={section}
          label={label}
          onClick={() => {
            onEditEntitySection(target, section);
            onDismiss();
          }}
        />
      ))}
    </SlideToolbarButtonWithPopover>
  );
}

export interface EditEntitySectionButtonProps {
  target: PresentationEntityActionTarget;
  /** The edit-entity modal section this button jumps straight to. */
  section: EditEntitySection;
  /** Button copy, e.g. "Edit current access" / "Edit dispositions". */
  label: string;
}

/**
 * Single-purpose "Edit <section>" toolbar button. Unlike {@link EditEntityButton}
 * (a dropdown of every section), this opens the edit-entity modal directly at a
 * single, caller-chosen {@link EditEntitySection} — used by the entity-overview
 * slides where each card maps to one specific edit view. Dismisses the hover
 * treatment on click so the floating toolbar doesn't linger behind the modal.
 * Renders disabled when the edit flow isn't wired up (e.g. the viewer can't edit).
 */
export function EditEntitySectionButton({
  target,
  section,
  label,
}: EditEntitySectionButtonProps) {
  const actions = usePresentationEntityActionsContext();
  const hover = usePresentationHoverActionsContext();
  const onEditEntitySection = actions?.onEditEntitySection;

  if (!onEditEntitySection) {
    return (
      <SlideToolbarButton variant="transparent" startIcon={Edit02Icon} disabled>
        {label}
      </SlideToolbarButton>
    );
  }

  return (
    <SlideToolbarButton
      variant="transparent"
      startIcon={Edit02Icon}
      onClick={() => {
        onEditEntitySection(target, section);
        hover?.dismissHover();
      }}
    >
      {label}
    </SlideToolbarButton>
  );
}

/**
 * "Open in app" toolbar button. Opens the entity's detail page in a new tab.
 * Shared by the entities-list rows and the estate-waterfall diagram nodes.
 */
export function OpenInAppButton({
  target,
  onDismiss,
  // Forward the group-divider props (`startsGroup`/`endsGroup`) to the
  // underlying `SlideToolbarButton` so `SlideToolbar` can place a divider next
  // to this button (e.g. after "Edit holdings" on ownership-diagram tiles).
  ...groupProps
}: PresentationEntityActionButtonProps) {
  const actions = usePresentationEntityActionsContext();
  const householdId = actions?.householdId;

  const openInApp = () => {
    if (!householdId) return;
    const path = getCompletePathFromRouteKey(
      ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS,
      { householdId, entityId: target.entityId }
    );
    window.open(path, '_blank', 'noopener,noreferrer');
    onDismiss();
  };

  return (
    <SlideToolbarButton
      variant="transparent"
      startIcon={LinkExternal01Icon}
      disabled={!householdId}
      onClick={openInApp}
      {...groupProps}
    >
      Open in app
    </SlideToolbarButton>
  );
}

/**
 * "Edit subtrust" toolbar button for testamentary-entity (sub-trust) nodes.
 * Opens the full-screen sub-trust trowser via {@link usePresentationEntityActionsContext}
 * (the provider mounts it outside any remount-keyed diagram subtree). Renders
 * disabled when the edit flow isn't wired up. Shared by the estate-waterfall and
 * entity-diagram tiles.
 */
export function EditSubtrustButton({
  testamentaryEntityId,
  onDismiss,
}: {
  testamentaryEntityId: string;
  onDismiss: () => void;
}) {
  const actions = usePresentationEntityActionsContext();
  const onEditSubtrust = actions?.onEditSubtrust;

  return (
    <SlideToolbarButton
      variant="transparent"
      startIcon={Edit02Icon}
      disabled={!onEditSubtrust}
      onClick={() => {
        onEditSubtrust?.(testamentaryEntityId);
        onDismiss();
      }}
    >
      Edit subtrust
    </SlideToolbarButton>
  );
}

/**
 * "Edit person" toolbar button for individual (client-profile) nodes. Opens the
 * client-profile side drawer via {@link usePresentationEntityActionsContext} (the
 * provider mounts it outside any remount-keyed diagram subtree). Renders disabled
 * when the edit flow isn't wired up.
 */
export function EditPersonButton({
  clientProfileId,
  onDismiss,
}: {
  clientProfileId: string;
  onDismiss: () => void;
}) {
  const actions = usePresentationEntityActionsContext();
  const onEditClientProfile = actions?.onEditClientProfile;

  return (
    <SlideToolbarButton
      variant="transparent"
      // Person-with-pencil icon, matching the family tree slide's "Edit person".
      startIcon={UserEditIcon}
      disabled={!onEditClientProfile}
      onClick={() => {
        onEditClientProfile?.(clientProfileId);
        onDismiss();
      }}
    >
      Edit person
    </SlideToolbarButton>
  );
}

/**
 * "Edit organization" toolbar button for client-organization nodes. Opens the
 * client-organization side drawer via {@link usePresentationEntityActionsContext}.
 * Renders disabled when the edit flow isn't wired up.
 */
export function EditOrganizationButton({
  organizationId,
  onDismiss,
}: {
  organizationId: string;
  onDismiss: () => void;
}) {
  const actions = usePresentationEntityActionsContext();
  const onEditClientOrganization = actions?.onEditClientOrganization;

  return (
    <SlideToolbarButton
      variant="transparent"
      startIcon={Edit02Icon}
      disabled={!onEditClientOrganization}
      onClick={() => {
        onEditClientOrganization?.(organizationId);
        onDismiss();
      }}
    >
      Edit organization
    </SlideToolbarButton>
  );
}

/**
 * "Show group details" toolbar button for aggregate group nodes. Opens the
 * "Group details" drawer (owned by {@link usePresentationEntityActionsContext})
 * listing the group's member entities. Available to view-only collaborators
 * too, since the drawer is read-only.
 */
export function ShowGroupDetailsButton({
  members,
  onDismiss,
}: {
  members: GroupNodeMember[];
  onDismiss: () => void;
}) {
  const actions = usePresentationEntityActionsContext();
  const onShowGroupDetails = actions?.onShowGroupDetails;

  return (
    <SlideToolbarButton
      variant="transparent"
      startIcon={Grid01Icon}
      disabled={!onShowGroupDetails}
      onClick={() => {
        onShowGroupDetails?.({ members });
        onDismiss();
      }}
    >
      Show group details
    </SlideToolbarButton>
  );
}

interface ViewSourceButtonProps {
  /**
   * The record whose citation to open — an entity, client profile, client
   * organization, or testamentary entity id. Looked up in the designer's
   * `viewSourceMap` unless `citation` is supplied.
   */
  recordId: string;
  /**
   * The record's citation, for surfaces that carry it on their own fragment
   * instead of reading the designer map (the entity cover slide, which is shared
   * with the entity preview modal). Omit to use the map.
   */
  citation?: PresentationViewSourceCitation | null;
  onDismiss: () => void;
}

/**
 * "View source" toolbar button. Opens the record's citation: the onboarding
 * suggestion's highlighted evidence, or an entity's source documents when there's
 * no suggestion.
 *
 * Unlike its siblings this renders NOTHING (rather than disabled) when there's
 * nothing to cite. The other actions always exist and are merely unavailable,
 * whereas a citation either exists or doesn't — a permanently-disabled button
 * would sit on most tiles forever.
 *
 * Don't give this `startsGroup`/`endsGroup`, and don't place it straight after an
 * `endsGroup` sibling: `SlideToolbar` computes dividers from `Children.toArray`
 * before anything renders, so an element that renders null still occupies a slot
 * and would strand a divider at the end of the toolbar.
 */
export function ViewSourceButton({
  recordId,
  citation,
  onDismiss,
}: ViewSourceButtonProps) {
  const { viewSource } = useViewSourceAction(recordId, citation);

  if (!viewSource) return null;

  return (
    <SlideToolbarButton
      variant="transparent"
      startIcon={FileSearch02Icon}
      data-testid="presentation-view-source-button"
      onClick={() => {
        viewSource();
        onDismiss();
      }}
    >
      View source
    </SlideToolbarButton>
  );
}

interface EditHoldingsButtonProps extends PresentationEntityActionButtonProps {
  /**
   * The entity whose holdings menu is built. Typed to the minimal holdings
   * fragment (liabilities + integration slice) so any slide carrying
   * `EntityValuationActions_Entity` — the entities list or the entity cover
   * slide — can render this button.
   */
  entity: EntityValuationActions_EntityFragment;
}

/**
 * "Edit holdings" dropdown. Its menu is built directly from the slide's
 * already-loaded entity — the slim holdings slice (liability names/IDs and
 * integration entities) rides along with the presentation, so there's no fetch
 * or cache read at hover time. Selecting an action hands off to the provider,
 * which fetches the full entity (the "enrich" step) and owns the resulting
 * modals. Shared by the entities-list rows and the entity cover slide.
 */
export function EditHoldingsButton({
  target,
  entity,
  onDismiss,
}: EditHoldingsButtonProps) {
  const actions = usePresentationEntityActionsContext();
  const isViewOnly = !useCanEditHouseholdData();
  const integrationIsViewOnly = !useCanManageIntegrationData();
  const onEditHoldingsAction = actions?.onEditHoldingsAction;

  if (!onEditHoldingsAction) {
    return null;
  }

  const integrationActions = getEntityIntegrationActions(
    entity,
    integrationIsViewOnly
  );
  const menuActions = getEntityValuationActionsBase(entity, {
    isViewOnly,
    hasIntegrationActions: !isEmpty(integrationActions),
  }).concat(integrationActions);

  return (
    <SlideToolbarButtonWithPopover
      variant="transparent"
      startIcon={CoinsSwap02Icon}
      label="Edit holdings"
      popperVariant="menuBelow"
    >
      {renderEntityValuationActionMenuItems({
        actions: menuActions,
        entity,
        // The full entity detail (needed by the action's modals) is fetched on
        // click; hand off just the id.
        onAction: (action) => {
          onEditHoldingsAction(target.entityId, action);
          onDismiss();
        },
      })}
    </SlideToolbarButtonWithPopover>
  );
}
